import {
  AlertCircle,
  CheckCircle2,
  Copy,
  ExternalLink,
  Link2,
  Unplug
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import {
  configurePlaneConnection,
  disconnectPlaneConnection,
  getPlaneConnection,
  type PlaneConnectionRow
} from '../api';
import { useFeedback } from '../contexts/FeedbackContext';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatRelativeDate } from '../utils/formatters';

type RequestError = {
  response?: { data?: { message?: string } };
  message?: string;
};
function errorMessage(error: unknown) {
  const failure = error as RequestError;
  return (
    failure.response?.data?.message ||
    failure.message ||
    'Could not save the Plane connection'
  );
}

export function PlaneConnectionPanel() {
  const { session } = useSession();
  const { activeOrganization } = useWorkspace();
  const activeOrganizationId = activeOrganization?.id;
  const { confirm, toast } = useFeedback();
  const [connection, setConnection] = useState<PlaneConnectionRow | null>(null);
  const [instanceUrl, setInstanceUrl] = useState('https://app.plane.so');
  const [workspaceSlug, setWorkspaceSlug] = useState('');
  const [projectScopes, setProjectScopes] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    if (!session?.accessToken) return;
    setConnection(null);
    setLoadError(false);
    try {
      const result = await getPlaneConnection(
        session.accessToken,
        activeOrganizationId
      );
      setConnection(result);
      if (result.status !== 'not_connected') {
        setInstanceUrl(result.instanceUrl || 'https://app.plane.so');
        setWorkspaceSlug(result.workspaceSlug || '');
        setProjectScopes(
          (result.projects || [])
            .map((project) => `${project.identifier}=${project.id}`)
            .join('\n')
        );
      }
    } catch {
      setLoadError(true);
    }
  }, [activeOrganizationId, session?.accessToken]);
  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!session?.accessToken) return;
    const projects = projectScopes
      .split(/\r?\n/)
      .map((line) => {
        const [identifier, id] = line.split('=').map((part) => part.trim());
        return { identifier: identifier?.toUpperCase() || '', id: id || '' };
      })
      .filter((project) => project.identifier && project.id);
    setBusy(true);
    try {
      const result = await configurePlaneConnection(session.accessToken, {
        organizationId: activeOrganizationId,
        instanceUrl,
        workspaceSlug,
        projects,
        webhookSecret: webhookSecret || undefined
      });
      setConnection(result);
      setEditing(false);
      setWebhookSecret('');
      toast(
        'success',
        'Plane work items will appear as webhook updates arrive'
      );
    } catch (error: unknown) {
      toast('error', errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (
      !session?.accessToken ||
      !(await confirm({
        title: 'Disconnect Plane?',
        message:
          'Cindro will stop accepting events from this webhook. Remove the webhook in Plane as well; existing delivery history will stay in Cindro.',
        confirmLabel: 'Disconnect',
        danger: true
      }))
    )
      return;
    setBusy(true);
    try {
      await disconnectPlaneConnection(
        session.accessToken,
        activeOrganizationId
      );
      setWebhookSecret('');
      await load();
      toast('success', 'Plane disconnected');
    } catch {
      toast('error', 'Could not disconnect Plane');
    } finally {
      setBusy(false);
    }
  };

  const copyWebhookUrl = async () => {
    if (!connection?.webhookUrl) return;
    try {
      await navigator.clipboard.writeText(connection.webhookUrl);
      toast('success', 'Webhook URL copied');
    } catch {
      toast('error', 'Could not copy the webhook URL');
    }
  };

  if (!connection)
    return (
      <section className="clickupConnection planeConnection">
        <div className="connectionHeading">
          <div className="connectionLogo planeConnectionLogo">P</div>
          <div>
            <strong>Plane</strong>
            <span>
              {loadError
                ? 'Connection settings unavailable'
                : 'Loading connection…'}
            </span>
          </div>
          {loadError ? (
            <button
              type="button"
              className="secondaryButton"
              onClick={() => void load()}
            >
              Try again
            </button>
          ) : null}
        </div>
      </section>
    );
  const connected = connection.status === 'active';
  const receiving = Boolean(connection.lastWebhookAt);

  return (
    <section className="clickupConnection planeConnection">
      <div className="connectionHeading">
        <div className="connectionLogo planeConnectionLogo">P</div>
        <div>
          <strong>Plane</strong>
          <span>Bring work items into delivery journeys</span>
        </div>
        <span
          className={`connectionState ${connected && receiving ? 'connectionState--healthy' : ''}`}
        >
          {connected && receiving ? (
            <CheckCircle2 size={13} />
          ) : (
            <AlertCircle size={13} />
          )}
          {connected
            ? receiving
              ? 'Webhook received'
              : 'Configured · waiting for first event'
            : 'Not connected'}
        </span>
      </div>

      {!connection.platformConfigured ? (
        <div className="connectionNotice">
          <AlertCircle size={16} />
          <div>
            <strong>Plane webhooks are not available yet</strong>
            <span>
              This Cindro deployment needs its public webhook address and
              connector encryption enabled.
            </span>
          </div>
        </div>
      ) : null}

      {connected && !editing ? (
        <div className="connectionHealth">
          <dl>
            <div>
              <dt>Workspace</dt>
              <dd>{connection.workspaceSlug}</dd>
            </div>
            <div>
              <dt>Projects</dt>
              <dd>
                {connection.projects
                  .map((project) => project.identifier)
                  .join(', ')}
              </dd>
            </div>
            <div>
              <dt>Latest webhook</dt>
              <dd>
                {connection.lastWebhookAt
                  ? formatRelativeDate(connection.lastWebhookAt)
                  : 'Waiting for first event'}
              </dd>
            </div>
            <div>
              <dt>Access</dt>
              <dd>Read-only events</dd>
            </div>
          </dl>
          <div className="planeConnectionNote">
            <strong>What Cindro receives</strong>
            <span>
              Only new work-item events for these project IDs. Cindro does not
              read task history, call the Plane API, or change Plane tasks.
            </span>
          </div>
          {connection.errorMessage ? (
            <div className="connectionNotice">
              <AlertCircle size={15} />
              <div>
                <strong>Connection needs attention</strong>
                <span>{connection.errorMessage}</span>
              </div>
            </div>
          ) : null}
          <div className="connectionActions">
            <button
              type="button"
              className="secondaryButton"
              disabled={busy}
              onClick={() => {
                setEditing(true);
                setWebhookSecret('');
              }}
            >
              Edit setup
            </button>
            <button
              type="button"
              className="textButton"
              disabled={busy}
              onClick={() => void disconnect()}
            >
              <Unplug size={14} />
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <div className="connectionSetup planeConnectionSetup">
          <div className="planeConnectionIntro">
            <div>
              <h3>Connect Plane without an API key</h3>
              <p>
                Cindro listens for signed work-item events. This works with
                Plane Cloud and self-hosted Community Edition, and does not give
                Cindro permission to edit tasks.
              </p>
            </div>
          </div>
          <ol className="planeConnectionSteps">
            <li>
              In Plane, open Workspace settings → Webhooks. Create a new v2
              webhook for work-item create, update and delete events, and paste
              the address below.
            </li>
            <li>
              Enter each project’s key and UUID as <code>KEY=UUID</code>. Plane
              work-item events carry the UUID; Cindro uses the key to link items
              to branches and pull requests.
            </li>
            <li>
              Copy the signing secret Plane shows when you create the webhook,
              paste it below, then connect. If you lose it, reveal or regenerate
              it in Plane. Plane can filter events too; Cindro still checks each
              project ID.
            </li>
          </ol>
          <label>
            <span>Cindro webhook address</span>
            <div className="planeWebhookAddress">
              <input
                value={connection.webhookUrl || ''}
                readOnly
                aria-label="Cindro webhook address"
              />
              <button
                type="button"
                className="secondaryButton"
                disabled={!connection.webhookUrl}
                onClick={() => void copyWebhookUrl()}
              >
                <Copy size={14} />
                Copy
              </button>
            </div>
          </label>
          <div className="trackRuleGrid">
            <label>
              <span>Plane instance URL</span>
              <input
                type="url"
                value={instanceUrl}
                onChange={(event) => setInstanceUrl(event.target.value)}
                placeholder="https://app.plane.so"
              />
            </label>
            <label>
              <span>Workspace slug</span>
              <input
                value={workspaceSlug}
                onChange={(event) => setWorkspaceSlug(event.target.value)}
                placeholder="my-workspace"
              />
            </label>
          </div>
          <label>
            <span>Project scope</span>
            <textarea
              rows={3}
              value={projectScopes}
              onChange={(event) => setProjectScopes(event.target.value)}
              placeholder={
                'SKL=59e3be42-87ec-4950-99a3-ae639cf2b089\nAPI=8c542c15-dcad-4fe8-9d7d-a20805c1a198'
              }
            />
            <small>
              One project per line. Use its short alphanumeric identifier (the
              prefix in SKL-42) and UUID from Plane’s project URL or details.
              Cindro accepts only these projects.
            </small>
          </label>
          <label>
            <span>
              Webhook signing secret
              {connected ? ' (leave blank to keep current)' : ''}
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={webhookSecret}
              onChange={(event) => setWebhookSecret(event.target.value)}
              placeholder="Paste the secret shown in Plane"
            />
          </label>
          <div className="planeConnectionSecurity">
            <AlertCircle size={15} />
            <span>
              Cindro encrypts the signing secret and does not display it again.
              Plane API keys are not requested. This connection starts with new
              events only: it does not import existing tasks or write back
              status changes. If the first update is missing, open Plane&apos;s
              webhook delivery log.
            </span>
          </div>
          <div className="connectionActions">
            <button
              type="button"
              className="primaryButton"
              disabled={
                busy ||
                !connection.platformConfigured ||
                !instanceUrl.trim() ||
                !workspaceSlug.trim() ||
                !projectScopes.trim() ||
                (!connected && !webhookSecret.trim())
              }
              onClick={() => void save()}
            >
              <Link2 size={14} />
              {busy ? 'Saving…' : connected ? 'Save changes' : 'Connect Plane'}
            </button>
            {connected ? (
              <button
                type="button"
                className="secondaryButton"
                disabled={busy}
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
            ) : null}
            <a
              href="https://developers.plane.so/dev-tools/intro-webhooks"
              target="_blank"
              rel="noreferrer"
              className="textButton"
            >
              Plane webhook guide
              <ExternalLink size={13} />
            </a>
          </div>
        </div>
      )}
    </section>
  );
}
